import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { apiaryService, hiveService, forageService } from '../services/firebase';
import { Apiary, ForageMarker } from '../types';
import WeatherApiaryCard from './WeatherApiaryCard';
import ApiariesMap from '../features/apiaries/ApiariesMap';
import './Dashboard.css';
import './WeatherApiaryCard.css';

// Simple forage source modal component
const ForageSourceModal: React.FC<{
  onClose: () => void;
  onSubmit: (type: string, season: string, notes: string) => void;
  position: {lat: number, lng: number} | null;
}> = ({ onClose, onSubmit, position }) => {
  const [type, setType] = useState('wildflower');
  const [season, setSeason] = useState('spring');
  const [notes, setNotes] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(type, season, notes);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '20px',
        maxWidth: '400px',
        width: '90%',
        maxHeight: '80vh',
        overflowY: 'auto'
      }}>
        <h3 style={{ marginTop: 0 }}>Add Forage Source</h3>
        {position && (
          <p style={{ fontSize: '0.9em', color: '#666', marginBottom: '15px' }}>
            Location: {position.lat.toFixed(4)}, {position.lng.toFixed(4)}
          </p>
        )}
        
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Type:
            </label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            >
              <option value="wildflower">Wildflower</option>
              <option value="tree_blossom">Tree Blossom</option>
              <option value="heather">Heather</option>
              <option value="rape">Rape (Canola)</option>
              <option value="linden">Linden</option>
              <option value="other">Other</option>
            </select>
          </div>
          
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Season:
            </label>
            <select
              value={season}
              onChange={(e) => setSeason(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            >
              <option value="spring">Spring</option>
              <option value="summer">Summer</option>
              <option value="autumn">Autumn</option>
              <option value="year_round">Year Round</option>
            </select>
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Notes:
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes about this forage source..."
              style={{
                width: '100%',
                height: '80px',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                resize: 'vertical'
              }}
            />
          </div>
          
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: 'white',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: '4px',
                backgroundColor: '#28a745',
                color: 'white',
                cursor: 'pointer'
              }}
            >
              Add Source
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface ApiariesPageWeatherCardsProps {
  onNavigateToInspections?: () => void;
}

const ApiariesPageWeatherCards: React.FC<ApiariesPageWeatherCardsProps> = ({ onNavigateToInspections }) => {
  const { user } = useAuth();
  const [apiaries, setApiaries] = useState<Apiary[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const [hiveCountMap, setHiveCountMap] = useState<Record<string, number>>({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingApiary, setEditingApiary] = useState<Apiary | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    registrationNumber: ''
  });
  
  // Forage sources state
  const [forageMarkers, setForageMarkers] = useState<ForageMarker[]>([]);
  const [selectedApiaryId, setSelectedApiaryId] = useState<string>('');
  const [showAddForageModal, setShowAddForageModal] = useState(false);
  const [newForageLocation, setNewForageLocation] = useState<{lat: number, lng: number} | null>(null);

  useEffect(() => {
    if (user) {
      loadApiaries();
    }
  }, [user]);

  // Auto-select first apiary when apiaries load
  useEffect(() => {
    if (apiaries.length > 0 && !selectedApiaryId) {
      setSelectedApiaryId(apiaries[0].id);
    }
  }, [apiaries, selectedApiaryId]);

  // Load forage markers
  const loadForageMarkers = async () => {
    if (!user) return;
    
    try {
      const markers = await forageService.getUserForageMarkers(user.uid);
      setForageMarkers(markers);
    } catch (error) {
      console.error('Error loading forage markers:', error);
    }
  };

  useEffect(() => {
    loadForageMarkers();
  }, [user]);

  // Forage marker handlers
  const handleAddForageMarker = async (lat: number, lng: number) => {
    if (!user) return;
    
    setNewForageLocation({ lat, lng });
    setShowAddForageModal(true);
  };

  const handleForageMarkerSubmit = async (type: string, season: string, notes: string) => {
    if (!user || !newForageLocation) return;
    
    try {
      const apiaryId = selectedApiaryId || (apiaries.length > 0 ? apiaries[0].id : '');
      
      await forageService.addForageMarker({
        apiaryId,
        lat: newForageLocation.lat,
        lng: newForageLocation.lng,
        type: type as any,
        season: season as any,
        notes
      });
      
      await loadForageMarkers();
      setShowAddForageModal(false);
      setNewForageLocation(null);
    } catch (error) {
      console.error('Error adding forage marker:', error);
      alert('Error adding forage source. Please try again.');
    }
  };

  const handleForageMarkerClick = (marker: ForageMarker) => {
    // Handle forage marker click - could show details or edit modal
    console.log('Forage marker clicked:', marker);
  };

  const handleEditForageMarker = (marker: ForageMarker) => {
    // Handle forage marker edit
    console.log('Edit forage marker:', marker);
  };

  const loadApiaries = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const userApiaries = await apiaryService.getUserApiaries(user.uid);
      
      // Fix apiaries with 0,0 coordinates by giving them realistic coordinates
      const fixedApiaries = userApiaries.map(apiary => {
        if (apiary.lat === 0 && apiary.lng === 0) {
          // Generate coordinates based on apiary name for consistency
          const nameHash = apiary.name.split('').reduce((a, b) => {
            a = ((a << 5) - a) + b.charCodeAt(0);
            return a & a;
          }, 0);
          
          // UK bounding box coordinates with some variation
          const ukLat = 51.0 + (Math.abs(nameHash) % 1000) / 1000 * 5; // 51-56 latitude
          const ukLng = -6.0 + (Math.abs(nameHash * 2) % 1000) / 1000 * 8; // -6 to +2 longitude
          
          return {
            ...apiary,
            lat: Number(ukLat.toFixed(4)),
            lng: Number(ukLng.toFixed(4))
          };
        }
        return apiary;
      });
      
      setApiaries(fixedApiaries);
      
      // Load hive counts for each apiary
      const counts: Record<string, number> = {};
      for (const apiary of fixedApiaries) {
        try {
          const hives = await hiveService.getApiaryHives(apiary.id);
          counts[apiary.id] = hives.length;
        } catch (error) {
          console.error(`Error loading hives for apiary ${apiary.id}:`, error);
          counts[apiary.id] = 0;
        }
      }
      setHiveCountMap(counts);
    } catch (error) {
      console.error('Error loading apiaries:', error);
      alert('Error loading apiaries. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    if (!formData.name.trim() || !formData.location.trim()) {
      alert('Please fill in all required fields.');
      return;
    }

    try {
      if (editingApiary) {
        // Update existing apiary
        const updates: Partial<Apiary> = {
          name: formData.name,
          location: formData.location
        };
        
        await apiaryService.updateApiary(editingApiary.id, updates);
        alert('Apiary updated successfully!');
      } else {
        // Add new apiary
        const newApiaryData: Omit<Apiary, 'id'> = {
          name: formData.name,
          location: formData.location,
          lat: 51.5074 + (Math.random() - 0.5) * 0.1, // London area with small variation
          lng: -0.1278 + (Math.random() - 0.5) * 0.1,
          created_at: new Date().toISOString(),
          user_id: user.uid
        };
        
        await apiaryService.addApiary(newApiaryData);
        alert('Apiary saved successfully!');
      }
      
      setFormData({ name: '', location: '', registrationNumber: '' });
      setShowAddForm(false);
      setEditingApiary(null);
      loadApiaries(); // Reload data to show changes
    } catch (error) {
      console.error('Error saving apiary:', error);
      alert(`Error saving apiary: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleEditApiary = (apiary: Apiary) => {
    setEditingApiary(apiary);
    setFormData({
      name: apiary.name,
      location: apiary.location,
      registrationNumber: ''
    });
    setShowAddForm(true);
  };

  const handleDeleteApiary = async (apiary: Apiary) => {
    if (!window.confirm(`Are you sure you want to delete "${apiary.name}"? This will also delete all associated hives and inspections.`)) {
      return;
    }

    try {
      await apiaryService.deleteApiary(apiary.id);
      alert('Apiary deleted successfully!');
      loadApiaries();
    } catch (error) {
      console.error('Error deleting apiary:', error);
      alert(`Error deleting apiary: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleInspectApiary = (apiary: Apiary) => {
    console.log('Inspect apiary:', apiary);
    // Navigate to inspections page instead of showing AI modal directly
    if (onNavigateToInspections) {
      onNavigateToInspections();
    }
  };

  const handleViewOnMap = (apiary: Apiary) => {
    console.log('View on map:', apiary);
    setViewMode('map');
  };

  const handleAddHive = (apiary: Apiary) => {
    console.log('Add hive to:', apiary);
    alert(`Adding hive to ${apiary.name}`);
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading your apiaries...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container" style={{ 
      minWidth: '1200px',
      width: '100%',
      overflowX: 'auto'
    }}>
      <div className="page-header" style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '20px',
        padding: '20px 0'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '24px', color: '#333' }}>
            🏞️ My Apiaries
          </h2>
          
          <div style={{ display: 'flex', gap: '10px' }}>
            <button 
              className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: '20px',
                backgroundColor: viewMode === 'list' ? '#4ecdc4' : '#e9ecef',
                color: viewMode === 'list' ? 'white' : '#666',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              📋 List View
            </button>
            <button 
              className={`view-toggle-btn ${viewMode === 'map' ? 'active' : ''}`}
              onClick={() => setViewMode('map')}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: '20px',
                backgroundColor: viewMode === 'map' ? '#4ecdc4' : '#e9ecef',
                color: viewMode === 'map' ? 'white' : '#666',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              🗺️ Map View
            </button>
            <button 
              className="view-toggle-btn"
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: '20px',
                backgroundColor: '#e9ecef',
                color: '#666',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              🔍 Inspections
            </button>
          </div>
        </div>

        <button 
          className="primary-btn" 
          onClick={() => {
            setFormData({ name: '', location: '', registrationNumber: '' });
            setEditingApiary(null);
            setShowAddForm(!showAddForm);
          }}
          style={{
            padding: '12px 24px',
            backgroundColor: '#4ecdc4',
            color: 'white',
            border: 'none',
            borderRadius: '25px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(78, 205, 196, 0.3)'
          }}
        >
          + ADD APIARY
        </button>
      </div>

      {showAddForm && (
        <div className="form-card" style={{ marginBottom: '30px' }}>
          <h3>{editingApiary ? 'Edit Apiary' : 'Add New Apiary'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Apiary Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="e.g., Garden Apiary, Field Site A"
                required
              />
            </div>
            <div className="form-group">
              <label>Location *</label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                placeholder="e.g., London, UK or postcode"
                required
              />
            </div>
            <div className="form-group">
              <label>BeeBase Registration Number</label>
              <input
                type="text"
                name="registrationNumber"
                value={formData.registrationNumber}
                onChange={handleInputChange}
                placeholder="e.g., UK123456"
              />
              <small>Optional - helps with BeeBase integration</small>
            </div>
            <div className="form-actions">
              <button 
                type="button" 
                onClick={() => {
                  setShowAddForm(false);
                  setEditingApiary(null);
                  setFormData({ name: '', location: '', registrationNumber: '' });
                }} 
                className="secondary-btn"
              >
                Cancel
              </button>
              <button type="submit" className="primary-btn">
                {editingApiary ? 'Update Apiary' : 'Add Apiary'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="content-section">
        {apiaries.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🏞️</div>
            <h3>No Apiaries Yet</h3>
            <p>Add your first apiary to start tracking your beehives with beautiful weather cards</p>
            <button 
              className="primary-btn" 
              onClick={() => setShowAddForm(true)}
            >
              Add Your First Apiary
            </button>
          </div>
        ) : viewMode === 'map' ? (
          <div>
            {/* Focus on Apiary Dropdown - moved above map */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '15px',
              padding: '10px 15px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e9ecef'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <label style={{ fontWeight: 'bold', color: '#495057' }}>Focus on Apiary:</label>
                <select
                  value={selectedApiaryId}
                  onChange={(e) => setSelectedApiaryId(e.target.value)}
                  style={{
                    border: '1px solid #ddd',
                    borderRadius: '5px',
                    padding: '8px 12px',
                    fontSize: '1em',
                    minWidth: '200px',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="">All Apiaries</option>
                  {apiaries.map(apiary => (
                    <option key={apiary.id} value={apiary.id}>
                      {apiary.name}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ fontSize: '0.9em', color: '#6c757d' }}>
                {selectedApiaryId ? 
                  `🎯 Focused on: ${apiaries.find(a => a.id === selectedApiaryId)?.name || 'Selected Apiary'}` :
                  '💡 Select an apiary to focus the map view'
                }
              </div>
            </div>
            
            <div style={{ display: 'flex', height: '900px', width: '100%', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
              {/* Map Container */}
              <div style={{ flex: 1, position: 'relative', overflowY: 'auto' }}>
                <ApiariesMap
                  apiaries={apiaries}
                  forageMarkers={forageMarkers}
                  selectedApiaryId={selectedApiaryId}
                  onApiaryClick={(apiary) => console.log('Clicked apiary:', apiary)}
                  onInspectApiary={handleInspectApiary}
                  onForageMarkerClick={handleForageMarkerClick}
                  onAddForageMarker={handleAddForageMarker}
                  onEditForageMarker={handleEditForageMarker}
                  showForageMarkers={true}
                  showBeeBaseAlerts={true}
                  showRiskAssessment={true}
                />
              </div>
            
            {/* Forage Sources Sidebar */}
            <div style={{
              width: '300px',
              backgroundColor: '#f8f9fa',
              borderLeft: '1px solid #e9ecef',
              padding: '20px',
              overflowY: 'auto'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h3 style={{ margin: 0, fontSize: '1.1em', color: '#495057' }}>Forage Sources</h3>
                <button
                  onClick={() => setShowAddForageModal(true)}
                  style={{
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '3px',
                    padding: '6px 12px',
                    fontSize: '0.8em',
                    cursor: 'pointer'
                  }}
                >
                  Add Source
                </button>
              </div>
              
              {/* Forage Summary */}
              <div style={{
                backgroundColor: 'white',
                borderRadius: '5px',
                padding: '12px',
                marginBottom: '15px',
                border: '1px solid #e9ecef'
              }}>
                <div style={{ fontSize: '0.9em', color: '#6c757d', marginBottom: '8px' }}>Summary</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '0.8em' }}>
                  <div>Total Sources: <strong>{forageMarkers.length}</strong></div>
                  <div>Spring: <strong>{forageMarkers.filter(m => m.season === 'spring').length}</strong></div>
                  <div>Summer: <strong>{forageMarkers.filter(m => m.season === 'summer').length}</strong></div>
                  <div>Autumn: <strong>{forageMarkers.filter(m => m.season === 'autumn').length}</strong></div>
                </div>
              </div>
              
              {/* Forage List */}
              <div style={{ fontSize: '0.9em' }}>
                {forageMarkers.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#6c757d', fontStyle: 'italic', padding: '20px 0' }}>
                    No forage sources yet.<br />Click on the map to add one.
                  </div>
                ) : (
                  forageMarkers.map(marker => (
                    <div key={marker.id} style={{
                      backgroundColor: 'white',
                      borderRadius: '5px',
                      padding: '10px',
                      marginBottom: '8px',
                      border: '1px solid #e9ecef',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleForageMarkerClick(marker)}
                    >
                      <div style={{ fontWeight: 'bold', fontSize: '0.9em', marginBottom: '4px' }}>
                        {marker.type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </div>
                      <div style={{ fontSize: '0.8em', color: '#6c757d' }}>
                        Season: {marker.season} • {marker.notes && `${marker.notes.substring(0, 30)}${marker.notes.length > 30 ? '...' : ''}`}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
            </div>
          </div>
        ) : (
          <div 
            className="weather-cards-grid" 
            style={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '20px',
              padding: '20px 0',
              justifyItems: 'center'
            }}
          >
            {apiaries.map((apiary) => (
              <WeatherApiaryCard
                key={apiary.id}
                apiary={apiary}
                hiveCount={hiveCountMap[apiary.id] || 0}
                onEdit={handleEditApiary}
                onDelete={handleDeleteApiary}
                onInspect={handleInspectApiary}
                onViewOnMap={handleViewOnMap}
                onAddHive={handleAddHive}
              />
            ))}
          </div>
        )}
      </div>
      
      {/* Add Forage Source Modal */}
      {showAddForageModal && (
        <ForageSourceModal
          onClose={() => setShowAddForageModal(false)}
          onSubmit={handleForageMarkerSubmit}
          position={newForageLocation}
        />
      )}
    </div>
  );
};

export default ApiariesPageWeatherCards;